import { FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import { FaFacebook, FaInstagram, FaXTwitter, FaTiktok } from 'react-icons/fa6';
//import { FaLinkedin } from 'react-icons/fa6';
import { Seo } from '../../core/seo';

const ABOUT_DESCRIPTION =
  'HirePanther is a Canadian marketplace that connects customers with independent local service providers. We make hiring trusted professionals easy, transparent, and safe.';

const AboutHirePanther = () => {
  return (
    <div className="text-[#284F60] min-h-screen py-12 px-6">
      <Seo
        title="About Us"
        description={ABOUT_DESCRIPTION}
        canonicalPath="/about-hirepanther"
      />
      {/* Rest of your component remains the same */}
      <div className="max-w-5xl mx-auto p-6 text-[#284F60] bg-white">
        {/* ===== Page Title ===== */}
        <h1 className="text-3xl font-bold mb-6 text-[#284F60] flex items-center gap-3">
          About HirePanther
        </h1>

        {/* ===== Subtitle ===== */}
        <p className="text-lg italic mb-2">
          Connecting Canadians with trusted local professionals.
        </p>
        <p className="text-sm text-gray-600 mb-8">
          <strong>Last Updated:</strong> December 01, 2025
        </p>

        {/* ===== Mission ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">Our Mission</h2>
          <p>
            At HirePanther, our mission is simple: to make hiring trusted
            service providers as easy, transparent, and safe as possible.
          </p>
          <p>
            Whether you need a home cleaner, a fitness trainer, a photographer,
            or a renovation expert — HirePanther helps you find qualified
            professionals, compare options, and book confidently.
          </p>
          <p>
            We bring together verified local providers and Canadian customers in
            one secure platform designed for convenience, fairness, and peace of
            mind.
          </p>
        </section>

        {/* ===== Platform ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">
            A Platform Built for Canadians
          </h2>
          <p>
            HirePanther was founded to support Canada’s growing independent
            workforce and the communities they serve. We operate across all
            provinces and territories, supporting both urban centers and local
            towns — helping Canadians hire safely, locally, and confidently.
          </p>
          <p>
            Our platform supports both English and French, follows Canadian
            privacy and tax laws (PIPEDA, GST/HST/QST compliance), and ensures
            transactions are transparent and protected.
          </p>
        </section>

        {/* ===== How It Works ===== */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            How HirePanther Works
          </h2>
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li>
              <strong>Search & Discover:</strong> Find trusted providers in your
              area or online by category, rating, and price.
            </li>
            <li>
              <strong>Book & Pay Securely:</strong> Confirm availability,
              receive instant quotes, and make payments safely through
              HirePanther.
            </li>
            <li>
              <strong>Enjoy Peace of Mind:</strong> All providers are verified,
              insured, and rated by real customers.
            </li>
            <li>
              <strong>Review & Rebook:</strong> Share your feedback and easily
              rebook your favourite providers for future jobs.
            </li>
          </ul>
        </section>

        {/* ===== Safety Section ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">
            Safety, Trust & Transparency
          </h2>
          <p>
            Your safety and satisfaction are our top priorities. Every provider
            on HirePanther goes through:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>
              Identity verification and background checks (where applicable)
            </li>
            <li>Insurance verification</li>
            <li>
              Service quality review and continuous performance monitoring
            </li>
          </ul>
          <p>
            All bookings are covered by HirePanther’s Safety & Insurance Policy,
            and payments are processed through secure Canadian payment gateways.
            We never release your payment until the job is marked complete.
          </p>
          <blockquote className="italic text-[#2D617A] border-l-4 border-[#2D617A] pl-4">
            “At HirePanther, we believe safety builds trust — and trust builds
            community.”
          </blockquote>
        </section>

        {/* ===== Empowering Businesses ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">
            Empowering Local Businesses
          </h2>
          <p>
            HirePanther isn’t just about convenience — it’s about empowering
            local service providers to grow their businesses.
          </p>
          <p>
            By using the platform, you’re supporting small businesses,
            tradespeople, and skilled professionals across Canada who bring
            passion, reliability, and pride to their work.
          </p>
          <p>
            Together, we’re helping Canada’s independent service economy thrive.
          </p>
        </section>

        {/* ===== Values Table ===== */}
        <section className="mb-8">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-4">
            Our Values
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300">
              <thead className="bg-[#E6EEF2]">
                <tr>
                  <th className="p-2 border border-gray-300 text-left">
                    Value
                  </th>
                  <th className="p-2 border border-gray-300 text-left">
                    What it Means
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border border-gray-300">Trust</td>
                  <td className="p-2 border border-gray-300">
                    Every profile is verified and backed by transparent reviews.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-gray-300">Fairness</td>
                  <td className="p-2 border border-gray-300">
                    Equal opportunity for providers and honest pricing for
                    customers.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-gray-300">Safety</td>
                  <td className="p-2 border border-gray-300">
                    Strong verification, insurance standards, and secure
                    transactions.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-gray-300">Simplicity</td>
                  <td className="p-2 border border-gray-300">
                    From booking to payout — everything in one place.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border border-gray-300">Community</td>
                  <td className="p-2 border border-gray-300">
                    Building a network where local businesses and customers
                    support each other.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* ===== Technology ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">
            Technology That Works for You
          </h2>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>Fast, intuitive search experience</li>
            <li>Secure payments and automatic invoices</li>
            <li>Smart matching between customers and qualified providers</li>
            <li>Real-time notifications and messaging</li>
            <li>Cross-platform access (web + mobile)</li>
          </ul>
        </section>

        {/* ===== Our Story ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">Our Story</h2>
          <p>
            HirePanther began with a simple frustration shared by thousands of
            Canadians: “Why is it so hard to find reliable, verified help for
            everyday tasks?”
          </p>
          <p>
            We set out to solve that — by creating a trusted digital marketplace
            where professionalism, safety, and convenience come first.
          </p>
          <p>
            Today, HirePanther connects thousands of customers and providers
            every month, saving time, supporting small businesses, and raising
            the standard for local services across Canada.
          </p>
        </section>

        {/* ===== Team ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">
            Meet the Team
          </h2>
          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>
              Developers and designers focused on innovation and security.
            </li>
            <li>Customer support specialists ready to help 7 days a week.</li>
            <li>
              Marketplace experts dedicated to fair, transparent practices.
            </li>
          </ul>
          <p>Together, we’re redefining how Canadians hire and work.</p>
        </section>

        {/* ===== Contact ===== */}
        <section className="mb-8 space-y-3">
          <h2 className="text-2xl font-semibold text-[#2D617A]">
            Get in Touch
          </h2>
          <p>We’d love to hear from you!</p>
          <div className="mt-4 space-y-2">
            <p className="flex items-center gap-2">
              <FaEnvelope className="text-indigo-600" />
              <a href="mailto:support@hirepanther.ca">
                support@hirepanther.ca
              </a>
            </p>
            <p className="flex items-center gap-2">
              <FaPhoneAlt className="text-indigo-600" />
              +1 (647) 960-0666
            </p>
          </div>
          {/* ===== Social Media Links ===== */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-[#2D617A] mb-3">
              Follow Us
            </h3>
            <div className="flex items-center gap-5">
              <a
                href="https://facebook.com/hirepanther"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#4267B2] hover:text-[#365899] transition-transform transform hover:scale-110"
              >
                <FaFacebook size={28} />
              </a>
              <a
                href="https://instagram.com/hirepanther"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#E4405F] hover:text-[#C13584] transition-transform transform hover:scale-110"
              >
                <FaInstagram size={28} />
              </a>
              {/*<a
                href="https://linkedin.com/company/hirepanther"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0077B5] hover:text-[#046293] transition-transform transform hover:scale-110"
              >
                <FaLinkedin size={28} />
              </a>*/}
              <a
                href="https://x.com/hirepanther"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-gray-700 transition-transform transform hover:scale-110"
              >
                <FaXTwitter size={28} />
              </a>
              <a
                href="https://www.tiktok.com/@hirepanther"
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-gray-700 transition-transform transform hover:scale-110"
              >
                <FaTiktok size={28} />
              </a>
            </div>
          </div>
        </section>

        {/* ===== Related Pages ===== */}
        <section className="mt-8">
          <h2 className="text-2xl font-semibold text-[#2D617A] mb-2">
            Related Pages
          </h2>
          <ul className="list-disc list-inside ml-4 space-y-1">
            {/* <li>
              <a href="/how-it-works">How It Works</a>
            </li> */}
            <li>
              <a href="/safety-insurance">Safety & Insurance</a>
            </li>
            <li>
              <a href="/privacy-policy">Privacy Policy</a>
            </li>
            <li>
              <a href="/terms-services">Terms of Service</a>
            </li>
            <li>
              <a href="/accessibility">Accessibility</a>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default AboutHirePanther;
